import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, taskHref, CLASS_STYLE, PRIORITY_LABELS, CLASSIFICATION_LABELS } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import DashboardTaskListModal from "@/components/DashboardTaskListModal";
import { Loader2, ArrowRight, FolderKanban, AlertCircle, CheckCircle2, Star, Flag, Activity, UserX, AlertTriangle } from "lucide-react";

const PRIO_DOT = { low: "text-slate-400", medium: "text-blue-500", high: "text-orange-500", urgent: "text-red-500" };
const slug = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso); const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "agora";
  if (s < 3600) return `há ${Math.floor(s / 60)} min`;
  if (s < 86400) return `há ${Math.floor(s / 3600)} h`;
  return d.toLocaleDateString("pt-BR");
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [bucketModal, setBucketModal] = useState(null); // { bucket, title, showImpediment }

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch((e) => setError(formatApiError(e)));
  }, []);

  const op = data?.operational;
  const openBucket = (bucket, title, showImpediment = false) => setBucketModal({ bucket, title, showImpediment });
  const goTask = (t) => navigate(taskHref(t));

  const KPIS = op ? [
    { label: "Projetos ativos", value: op.active_projects, icon: FolderKanban, to: "/projetos" },
    { label: "Tarefas atrasadas", value: op.overdue_tasks, icon: AlertCircle, danger: op.overdue_tasks > 0, onClick: () => openBucket("overdue", "Tarefas atrasadas"), hint: "Vencidas e não concluídas" },
    { label: "Minhas tarefas", value: op.my_tasks_count, icon: CheckCircle2, onClick: () => openBucket("mine", "Minhas tarefas") },
    { label: "MVP — Essenciais", value: `${op.essential_pct}%`, icon: Star, onClick: () => openBucket("scope_E", "Essenciais pendentes"), hint: `${op.essential_pending} ainda pendentes` },
    { label: "Tarefas sem responsável", value: op.unassigned_count, icon: UserX, onClick: () => openBucket("unassigned", "Tarefas sem responsável") },
    { label: "Tarefas impedidas", value: op.impeded_count, icon: AlertTriangle, danger: op.impeded_count > 0, onClick: () => openBucket("impeded", "Tarefas impedidas", true) },
  ] : [];

  const card = (k) => {
    const clickable = !!k.to || !!k.onClick;
    const act = k.onClick || (k.to ? () => navigate(k.to) : undefined);
    return (
      <div key={k.label} data-testid={`dashboard-kpi-${slug(k.label)}`}
        role={clickable ? "button" : undefined} tabIndex={clickable ? 0 : undefined}
        onClick={act} onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); act(); } } : undefined}
        className={`bg-white rounded-xl border border-slate-200 shadow-sm p-6 ${clickable ? "cursor-pointer hover:shadow-md hover:border-slate-400 hover:-translate-y-0.5 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900" : ""}`}>
        <div className="flex items-center justify-between">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{k.label}</div>
          <k.icon className={`w-4 h-4 ${k.danger ? "text-red-400" : "text-slate-300"}`} />
        </div>
        <div className={`mt-3 font-heading text-3xl font-semibold tracking-tight ${k.danger ? "text-red-600" : "text-slate-900"}`}>{k.value}</div>
        {k.hint && <div className="text-xs text-slate-400 mt-1">{k.hint}</div>}
      </div>
    );
  };

  const scopeOrder = ["E", "D", "O", "Q"];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Visão geral</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-2">Dashboard</h1>
        <p className="text-slate-500 mt-1">Visão geral dos projetos, sprints e tarefas que você pode acessar.</p>
      </div>

      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

      {!data ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Carregando...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">{KPIS.map(card)}</div>

          {op && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="dashboard-my-tasks">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-heading text-xl font-semibold">Minhas tarefas</h2>
                    <button onClick={() => openBucket("mine", "Minhas tarefas")} className="text-xs text-slate-400 hover:text-slate-700">Ver todas ({op.my_tasks_count})</button>
                  </div>
                  {op.my_tasks.length === 0 ? (
                    <p className="text-sm text-slate-400 py-6 text-center">Nada atribuído a você. Aproveite!</p>
                  ) : (
                    <ul className="divide-y divide-slate-100">
                      {op.my_tasks.map((t) => (
                        <li key={t.id} onClick={() => goTask(t)} data-testid={`dash-task-${t.id}`}
                          className="py-2.5 flex items-center gap-3 cursor-pointer hover:bg-slate-50/60 -mx-2 px-2 rounded-lg">
                          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-md text-xs font-semibold ${CLASS_STYLE[t.classification]}`}>{t.classification}</span>
                          <span className="flex-1 text-sm text-slate-800 truncate">{t.title}</span>
                          <span className="text-[11px] text-slate-400">{t.location === "kanban" ? "Kanban" : t.location === "impediment" ? "Impedida" : "Backlog"}</span>
                          <span className={`text-xs inline-flex items-center gap-1 ${PRIO_DOT[t.priority]}`}><span className="w-1.5 h-1.5 rounded-full bg-current" />{PRIORITY_LABELS[t.priority]}</span>
                          <ArrowRight className="w-4 h-4 text-slate-300" />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="dashboard-active-sprints">
                  <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2"><Flag className="w-4 h-4 text-slate-400" /> Sprints ativas</h2>
                  {op.active_sprints.length === 0 ? (
                    <p className="text-sm text-slate-400">Nenhuma sprint ativa.</p>
                  ) : (
                    <ul className="space-y-2">
                      {op.active_sprints.map((s) => (
                        <li key={s.id} onClick={() => navigate(`/projetos/${s.project_id}/sprints`)} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50/60 -mx-2 px-2 py-1.5 rounded-lg" data-testid={`dash-sprint-${s.id}`}>
                          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color || "#64748b" }} />
                          <div className="min-w-0"><div className="font-medium text-slate-800 truncate">{s.name}</div><div className="text-xs text-slate-400 truncate">{s.project_name}</div></div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="dashboard-scope">
                  <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2"><Star className="w-4 h-4 text-slate-400" /> Escopo do MVP</h2>
                  <ul className="space-y-2">
                    {scopeOrder.map((k) => {
                      const sc = op.scope_distribution[k] || { pending: 0, total: 0 };
                      return (
                        <li key={k} role="button" tabIndex={0} data-testid={`dash-scope-${k}`}
                          onClick={() => openBucket(`scope_${k}`, `${CLASSIFICATION_LABELS[k]} — pendentes`)}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openBucket(`scope_${k}`, `${CLASSIFICATION_LABELS[k]} — pendentes`); } }}
                          className="flex items-center gap-3 cursor-pointer hover:bg-slate-50/60 -mx-2 px-2 py-1.5 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900">
                          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-md text-xs font-semibold ${CLASS_STYLE[k]}`}>{k}</span>
                          <span className="flex-1 text-sm text-slate-700">{CLASSIFICATION_LABELS[k]}</span>
                          <span className="text-sm"><span className="font-semibold text-slate-900">{sc.pending} pendentes</span> <span className="text-slate-400">de {sc.total}</span></span>
                          <ArrowRight className="w-4 h-4 text-slate-300" />
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="dashboard-activity">
                  <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2"><Activity className="w-4 h-4 text-slate-400" /> Atividade recente</h2>
                  {op.recent_activity.length === 0 ? (
                    <p className="text-sm text-slate-400">Sem atividades recentes.</p>
                  ) : (
                    <ul className="space-y-3">
                      {op.recent_activity.map((a, i) => (
                        <li key={i} onClick={() => a.task_id && goTask(a)} data-testid={`dash-activity-${i}`}
                          className={`text-sm text-slate-600 flex items-start gap-2 ${a.task_id ? "cursor-pointer hover:bg-slate-50/60 -mx-2 px-2 py-1 rounded-lg" : ""}`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-2 shrink-0" />
                          <span><span className="font-medium text-slate-800">{a.by_name}</span> {a.verb} <span className="text-slate-800">“{a.title}”</span> · <span className="text-slate-400">{timeAgo(a.at)}</span></span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {bucketModal && <DashboardTaskListModal {...bucketModal} onClose={() => setBucketModal(null)} />}
    </div>
  );
}
